import supabase from '../lib/supabase';

const DEVICE_ID_KEY = 'votenations_device_id';
const LOCKED_COUNTRIES_KEY = 'votenations_locked_countries';

/**
 * Stable per-device identifier passed as p_ip_hash.
 * TODO: Replace with real IP-hash detection via a server-side edge function for
 * production-grade enforcement. A client-generated ID can be cleared/reset by the
 * user (localStorage-level protection only). Server-side lock enforcement still
 * applies once the identity exists in voter_identities.
 */
export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/** Last-known lock state from cast_vote responses (server-synced cache). */
export function getCachedLockedCountries() {
  const saved = localStorage.getItem(LOCKED_COUNTRIES_KEY);
  return saved ? JSON.parse(saved) : [];
}

export function cacheLockedCountries(lockedCountry1, lockedCountry2) {
  const countries = [lockedCountry1, lockedCountry2].filter(Boolean);
  localStorage.setItem(LOCKED_COUNTRIES_KEY, JSON.stringify(countries));
  return countries;
}

export async function castVote({ votingMapId, entityCode, optionChoice, country, ipHash }) {
  const { data, error } = await supabase.rpc('cast_vote', {
    p_voting_map_id: votingMapId,
    p_entity_code: entityCode,
    p_option_choice: optionChoice,
    p_country: country,
    p_ip_hash: ipHash ?? getDeviceId(),
  });

  if (error) throw error;
  return data;
}

export async function getVoteTally(votingMapId) {
  const { data, error } = await supabase.rpc('get_vote_tally', {
    p_voting_map_id: votingMapId,
  });

  if (error) throw error;
  return data ?? [];
}

/** Countries this voter identity has already used on a specific map. */
export async function getVoterMapVotes(votingMapId, ipHash) {
  const { data, error } = await supabase.rpc('get_voter_map_votes', {
    p_voting_map_id: votingMapId,
    p_ip_hash: ipHash ?? getDeviceId(),
  });

  if (error) throw error;
  return data ?? [];
}

/** Merge RPC tally rows into map display state (votes + per-entity coloring). */
export function applyTallyToMap(mapData, tallyRows) {
  let votesA = 0;
  let votesB = 0;
  const countryVotes = {};

  for (const row of tallyRows) {
    const aCount = Number(row.option_a_count) || 0;
    const bCount = Number(row.option_b_count) || 0;
    votesA += aCount;
    votesB += bCount;

    if (aCount > bCount) {
      countryVotes[row.entity_code] = 'A';
    } else if (bCount > aCount) {
      countryVotes[row.entity_code] = 'B';
    }
  }

  return {
    ...mapData,
    votesA,
    votesB,
    countryVotes,
  };
}
